import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { BrandMark, PoweredBy } from "../design/BrandMark";

import { send } from "../api/auth";
import { apply, type ApplicationForm } from "../api/apply";
import { StatusChip } from "../design/StatusChip";
import { PreSessionLocale, useT } from "../i18n/LocaleProvider";
import { useSession } from "../session/SessionProvider";
import { Challenge, type ChallengeHandle } from "../system/Turnstile";
import { Message } from "./Message";

// ---------------------------------------------------------------------------
// Sweep §4 / 0215. /apply/<token> — a person asks an agency for work.
//
// NOBODY IS KNOWN, so the words follow the browser's language (S63's rule for a
// screen before sign-in). The agency's name and its roles come from the server;
// the screen spells neither.
//
// THE SCREEN MUST NOT BE MORE HELPFUL THAN THE SERVER. The server answers the
// same whether it stored the application or already had one from this address,
// so "thank you" is all this page can honestly say, and it says no more.
//
// ONLY WHAT IS NEEDED TO DECIDE. No free-text box: a box like that is where
// health and criminal-record data arrive unasked (0215 decision 8).
// ---------------------------------------------------------------------------

/** The product's privacy notice. The agency's own notice is an owner decision (sweep S100). */
const PRIVACY_NOTICE = "https://verbunk.app/privacy";

export function Apply() {
  return (
    <PreSessionLocale>
      <ApplyScreen />
    </PreSessionLocale>
  );
}

type Load = { state: "loading" } | { state: "linkGone" } | { state: "unreachable" } | { state: "open"; form: ApplicationForm };

function ApplyScreen() {
  const { t } = useT();
  const token = useParams()["token"] ?? "";
  const { apiBase } = useSession();
  const [load, setLoad] = useState<Load>({ state: "loading" });
  const [done, setDone] = useState(false);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const res = await send<Partial<ApplicationForm>>(apply.form(apiBase, token));
        if (!live) return;
        if (res.status === 200 && Array.isArray(res.body.roles)) setLoad({ state: "open", form: res.body as ApplicationForm });
        else setLoad({ state: res.status === 404 ? "linkGone" : "unreachable" });
      } catch {
        if (live) setLoad({ state: "unreachable" });
      }
    })();
    return () => {
      live = false;
    };
  }, [apiBase, token]);

  if (load.state === "loading") return <Message tone="neutral" status={t("Working")} title={t("Loading…")} />;
  if (load.state === "linkGone") {
    return (
      <Message tone="neutral" status={t("Closed")} title={t("This link is not taking applications")}>
        <p>{t("Ask the agency that gave it to you for a new one.")}</p>
      </Message>
    );
  }
  if (load.state === "unreachable") {
    return (
      <Message tone="failed" status={t("Not loaded")} title={t("We could not reach Verbunk.")}>
        <p>{t("We could not reach Verbunk. Check your connection and try again.")}</p>
      </Message>
    );
  }
  if (done) {
    return (
      <Message tone="live" status={t("Sent")} title={t("Thank you")}>
        <p>{t("If {agency} has work for you, they will email you an invitation to sign in.", { agency: load.form.organisation })}</p>
      </Message>
    );
  }
  return <ApplyForm token={token} form={load.form} onSent={() => setDone(true)} />;
}

function ApplyForm({ token, form, onSent }: { token: string; form: ApplicationForm; onSent: () => void }) {
  const { t } = useT();
  const { apiBase } = useSession();
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [locality, setLocality] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [rightToWork, setRightToWork] = useState<boolean | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const challenge = useRef<ChallengeHandle | null>(null);

  async function submit() {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(
        apply.submit(apiBase, token, {
          given_name: givenName,
          family_name: familyName,
          email,
          phone,
          home_locality: locality,
          role_ids: roles,
          right_to_work: rightToWork === true,
          consent,
          turnstile_token: challengeToken ?? undefined,
        }),
      );
      if (res.status !== 200) {
        // A Turnstile token is spent once siteverify has seen it (SignUp's rule).
        challenge.current?.reset();
        setNotice(res.body.error?.message ?? t("That could not be sent. Try again."));
        return;
      }
      onSent();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  const toggle = (id: string) => setRoles((now) => (now.includes(id) ? now.filter((r) => r !== id) : [...now, id]));

  return (
    <main className="centre-page">
      <BrandMark agency={form.organisation} />
      <div className="centre-card">
        <h1>{t("Work for {agency}", { agency: form.organisation })}</h1>
        <p className="muted">{t("Tell them who you are and what work you want. It takes a minute.")}</p>

        <div className="form-message" role="status" aria-live="polite">
          {notice ? (
            <>
              <StatusChip tone="failed" label={t("Not sent")} />
              <span>{notice}</span>
            </>
          ) : null}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="field">
            <label htmlFor="apply-given">{t("First name")}</label>
            <input id="apply-given" autoComplete="given-name" required maxLength={100} value={givenName} onChange={(e) => setGivenName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="apply-family">{t("Last name")}</label>
            <input id="apply-family" autoComplete="family-name" required maxLength={100} value={familyName} onChange={(e) => setFamilyName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="apply-email">{t("Email address")}</label>
            <input
              id="apply-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              maxLength={320}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="field-hint">{t("The invitation to sign in goes here if they take you on.")}</p>
          </div>
          <div className="field">
            <label htmlFor="apply-phone">{t("Phone number (optional)")}</label>
            <input id="apply-phone" type="tel" autoComplete="tel" maxLength={30} value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="apply-locality">{t("Town or city you live in (optional)")}</label>
            <input id="apply-locality" autoComplete="address-level2" maxLength={120} value={locality} onChange={(e) => setLocality(e.target.value)} />
          </div>

          <fieldset className="field">
            <legend>{t("The work you want")}</legend>
            {form.roles.length === 0 ? <p className="muted">{t("This agency has not listed any work yet.")}</p> : null}
            {form.roles.map((r) => (
              <label key={r.id} className="check">
                <input type="checkbox" checked={roles.includes(r.id)} onChange={() => toggle(r.id)} /> {r.name}
              </label>
            ))}
          </fieldset>

          <fieldset className="field">
            <legend>{t("Do you have the right to work here?")}</legend>
            <label className="check">
              <input type="radio" name="apply-rtw" required checked={rightToWork === true} onChange={() => setRightToWork(true)} /> {t("Yes")}
            </label>
            <label className="check">
              <input type="radio" name="apply-rtw" checked={rightToWork === false} onChange={() => setRightToWork(false)} /> {t("No")}
            </label>
            <p className="field-hint">{t("Your own answer. The agency checks your documents before your first shift.")}</p>
          </fieldset>

          <div className="field">
            <label className="check">
              <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} />{" "}
              {t("I agree that {agency} may use these details to decide whether to offer me work. If they do not, the details are deleted after six months.", {
                agency: form.organisation,
              })}
            </label>
            <p className="field-hint">
              <a href={PRIVACY_NOTICE} target="_blank" rel="noreferrer">
                {t("How your details are handled")}
              </a>
            </p>
          </div>

          <Challenge onToken={setChallengeToken} handle={challenge} />

          <button className="btn btn-block" type="submit" disabled={busy || roles.length === 0}>
            {busy ? t("Working…") : t("Send my application")}
          </button>
        </form>
      </div>
      <PoweredBy />
    </main>
  );
}
